import { MAX_UPCS } from "./types";

import type {
  BrandMetrics,
  CampaignImpact,
  CampaignStats,
  CatalogSearch,
  CampaignUpcs,
  PlatformStats,
  ProductDemand,
  ProductFilters,
  ProductRaw,
  RetailerCategories,
  RetailerOverview,
  RetailerProducts,
  RetailerRedemptions,
  RetailerTraffic,
  ShopperGrowth,
  UpcMetrics,
  UserPhone,
} from "./types";

/**
 * Bundled sample data for local development before gft-insights-api exists
 * (or when working offline). Enabled with VITE_API_MOCK=true.
 * Shapes and values mirror the Looker Studio dashboards this app replaces.
 */

function daysBack(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function hoursBack(n: number): string {
  return new Date(Date.now() - n * 3_600_000).toISOString();
}

const STORE_NAMES = [
  "Aguila Supermarket III",
  "America's Food Basket Bowdoin",
  "America's Food Basket Rockaway",
  "Amex Food",
  "Amigos Supermarket",
  "Arco Grocery",
  "Armenia Fresh Market Inc.",
  "Azteca Corner Store",
  "Azteca Store #5 Inc.",
  "Bandi Foods Wholesale",
  "Bloomingdale Latin Market",
  "Bravo Supermarket Curryford",
  "Caribbean Best Market 1 (fka 4140 N STATE LLC)",
  "Caribbean Club Tampa",
  "Carniceria La Calentana 2",
  "Carniceria Monterrey #1 Immokalee",
  "El Famoso Borinquen Meat Market",
  "Hola Supermarket Orlando",
  "Ideal Foods Dania",
  "Jomara Wholesale",
  "Kissimmee Meat & Produce, Inc.",
  "Latinos Supermarket",
  "Los Corrales",
  "Riverview Fresh Market",
];

export function mockPlatformStats(): PlatformStats {
  const storeStatus = STORE_NAMES.map((storeName, i) => {
    const laneCount = 1 + (i % 6);
    const stale = i === 11; // one store with a weeks-old ping
    // Every store gets the $0.10 cut; stores with an agreement on record
    // also pay a vendor fee on top.
    const waterfallFeesPerTransaction = i % 5 === 4 ? 0.1 : 0.25;
    return {
      storeName,
      // Most stores share one installer; a few have none on record.
      posVendor: i % 5 === 4 ? null : "Blue Technology Group",
      waterfallFeesPerTransaction,
      profitPerTransaction: 0.75 - waterfallFeesPerTransaction,
      lastPingedAt: stale ? hoursBack(24 * 20) : hoursBack((i % 5) * 3),
      lastCheckoutAt: stale ? null : hoursBack((i % 7) * 5),
      laneCount,
      activeCampaignCount: i % 3 === 0 ? 0 : 1,
      totalTransactions: 1_500 + i * 1_637,
    };
  });

  const laneStatus = storeStatus.flatMap((store, i) =>
    Array.from({ length: store.laneCount }, (_, lane) => ({
      storeName: store.storeName,
      lane: String(lane + 1),
      pluginId: `gftrewardsplugin_v1.0.0.${29 + ((i + lane) % 3)}`,
      lastPingedAt: store.lastPingedAt,
      lastCheckoutAt: store.lastCheckoutAt,
      totalTransactions: Math.round(store.totalTransactions / store.laneCount),
    })),
  );

  const dmas = [
    "Orlando-Daytona Beach-Melbourne",
    "Tampa-St. Petersburg (Sarasota)",
    "Miami-Fort Lauderdale",
    "Fort Myers-Naples",
    "New York",
    "Boston (Manchester)",
    "No DMA assigned",
  ];
  const storesByDma = STORE_NAMES.map((storeName, i) => ({
    dma: dmas[i % dmas.length],
    storeName,
    units: 250_000 - i * 9_000,
  }));

  return {
    generatedAt: new Date().toISOString(),
    revenuePerTransaction: 0.75,
    tiles: {
      shoppersWithPhoneAllTime: 48_212,
      shoppersWithWalletAllTime: 31_907,
      shoppersOptedInForMarketingAllTime: 29_640,
      storeCount: 101,
      storesPingedLastDay: 93,
      storesCheckedOutLastDay: 92,
      laneCount: 327,
      transactionCount30d: 1_063_013,
      identifiedTransactionCount30d: 2_014,
      uniqueIdentifiedUsers30d: 1_375,
    },
    storeStatus,
    laneStatus,
    storesByDma,
  };
}

export function mockCampaignUpcs(): CampaignUpcs {
  const rows = [
    { campaignName: "$0.25 off 4.6 OZ can Libby's Chicken Vienna Sausage", upc: "00039000086691", brandName: "Libby's", subcategoryName: "Food & Beverage -> Pantry" },
    { campaignName: "$0.25 off Carnation Evaporated Milk 12 Oz", upc: "050000010110", brandName: "Carnation", subcategoryName: "Food & Beverage -> Pantry" },
    { campaignName: "$0.25 off Carnation Evaporated Milk 12 Oz", upc: "00050000159918", brandName: "Carnation", subcategoryName: "Food & Beverage -> Pantry" },
    { campaignName: "$0.50 OFF 20.00 OZ Nature's Own Butterbread", upc: "072250049191", brandName: "Nature's Own", subcategoryName: "Food & Beverage -> Pantry" },
    { campaignName: "$0.50 OFF Crest Tartar Protection Whitening Toothpaste in Cool Mint (5.7 oz)", upc: "00037000511977", brandName: null, subcategoryName: null },
    { campaignName: "$0.50 OFF Minute Maid Punch - various flavors", upc: "00002500004769", brandName: "Minute Maid", subcategoryName: "Food & Beverage -> Beverages" },
    { campaignName: "$0.50 OFF Minute Maid Punch - various flavors", upc: "00002500004772", brandName: "Minute Maid", subcategoryName: "Food & Beverage -> Beverages" },
    { campaignName: "GFT Validation Campaign", upc: null, brandName: null, subcategoryName: null },
  ];
  const dmas = ["Orlando-Daytona Beach-Melbourne", "Miami-Fort Lauderdale", null];
  return {
    generatedAt: new Date().toISOString(),
    upcs: rows.map((row, i) => ({ ...row, dma: dmas[i % dmas.length] })),
  };
}

export function mockUserPhone(userId: string): UserPhone {
  if (userId.startsWith("0")) {
    return { userId, phoneNumber: null };
  }
  return { userId, phoneNumber: "+541159731629" };
}

const GROWTH_WINDOW_WEEKS = 26;

/** Monday 00:00 UTC of the week containing `d`. */
function mondayUtc(d: Date): Date {
  const day = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  day.setUTCDate(day.getUTCDate() - ((day.getUTCDay() + 6) % 7));
  return day;
}

/**
 * storeId is a comma-separated list, matching the real endpoint. Weekly
 * signup counts for the window plus the current week to date, with an all-time
 * baseline, per store and summed.
 */
export function mockShopperGrowth(storeId?: string): ShopperGrowth {
  const now = new Date();
  const currentWeek = mondayUtc(now);
  const weekStart = (i: number) =>
    new Date(currentWeek.getTime() - (GROWTH_WINDOW_WEEKS - i) * 7 * 86_400_000)
      .toISOString()
      .slice(0, 10);
  // The last entry is the current week to date, scaled by how far into the
  // week it is (Monday 1/7 … Sunday 7/7).
  const weekFraction = (((now.getUTCDay() + 6) % 7) + 1) / 7;
  // Seeded so the numbers are the same on every load, and generated before
  // filtering so a store shows the same figures alone as in the total.
  let seed = 7;
  const rand = () => {
    seed = (seed * 1_103_515_245 + 12_345) % 2_147_483_648;
    return seed / 2_147_483_648;
  };
  const wanted = storeId
    ? new Set(storeId.split(",").map((s) => s.trim()).filter(Boolean))
    : null;
  const perStore = STORE_NAMES.map((storeName, i) => {
    const base = 8 + ((i * 7) % 30);
    const rate = 0.45 + ((i * 13) % 35) / 100;
    const weeks = Array.from({ length: GROWTH_WINDOW_WEEKS + 1 }, (_, w) => {
      const partial = w === GROWTH_WINDOW_WEEKS ? weekFraction : 1;
      const registered = Math.round(base * (0.7 + 0.6 * rand()) * partial);
      return {
        registered,
        optedIn: Math.round(registered * (rate + (rand() - 0.5) * 0.1)),
      };
    });
    const baseRegistered = base * 40 + Math.round(rand() * 200);
    return {
      storeId: String(1000 + i),
      storeName,
      weeks,
      baseline: {
        registered: baseRegistered,
        optedIn: Math.round(baseRegistered * rate),
      },
    };
  }).filter((s) => !wanted || wanted.has(s.storeId));

  const sum = (pick: (s: (typeof perStore)[number]) => number) =>
    perStore.reduce((acc, s) => acc + pick(s), 0);
  return {
    generatedAt: new Date().toISOString(),
    windowWeeks: GROWTH_WINDOW_WEEKS,
    windowStart: weekStart(0),
    currentWeekStart: weekStart(GROWTH_WINDOW_WEEKS),
    baseline: {
      registered: sum((s) => s.baseline.registered),
      optedIn: sum((s) => s.baseline.optedIn),
    },
    weekly: Array.from({ length: GROWTH_WINDOW_WEEKS + 1 }, (_, w) => ({
      weekStart: weekStart(w),
      registered: sum((s) => s.weeks[w].registered),
      optedIn: sum((s) => s.weeks[w].optedIn),
    })),
    stores: perStore.map((s) => {
      const periodRegistered = s.weeks.reduce((a, w) => a + w.registered, 0);
      const periodOptedIn = s.weeks.reduce((a, w) => a + w.optedIn, 0);
      return {
        storeId: s.storeId,
        storeName: s.storeName,
        registered: s.baseline.registered + periodRegistered,
        optedIn: s.baseline.optedIn + periodOptedIn,
        periodRegistered,
        periodOptedIn,
      };
    }),
  };
}

/** storeId is a comma-separated list, matching the real endpoint. */
export function mockRetailerRedemptions(storeId?: string): RetailerRedemptions {
  const storeIds = storeId
    ? new Set(storeId.split(",").map((s) => s.trim()).filter(Boolean))
    : null;
  const campaigns = [
    { campaignName: "$0.75 off Coca-Cola 2-Liter Bottle - multiple varieties", startDate: "2026-05-20", endDate: "2026-10-01", perStore: 300 },
    { campaignName: "$0.50 off Coca-Cola 20 oz and 2 L bottles - all flavors", startDate: "2026-05-26", endDate: "2026-10-01", perStore: 560 },
    { campaignName: "$0.25 off Carnation Evaporated Milk 12 Oz", startDate: "2026-05-20", endDate: "2026-07-13", perStore: 410 },
  ];
  // Mock store ids mirror mockRetailerFilters(): String(1000 + index).
  const redemptionsByCampaign = STORE_NAMES.slice(0, 12)
    .filter((_, i) => !storeIds || storeIds.has(String(1000 + i)))
    .flatMap((storeName, i) =>
      campaigns.slice(0, 1 + (i % 3)).map((c, j) => {
        const redemptions = Math.max(12, c.perStore - i * 41 - j * 17);
        return {
          storeName,
          campaignName: c.campaignName,
          startDate: c.startDate,
          endDate: c.endDate,
          redemptions,
          earnedByStore: Math.round(redemptions * 10) / 100,
          // Fees plus a ~$0.35 mock reward per redemption.
          totalPaidToStore: Math.round(redemptions * 45) / 100,
        };
      }),
    );
  return {
    generatedAt: new Date().toISOString(),
    windowDays: 180,
    redemptionsByCampaign,
    redemptionsOverTime: Array.from({ length: 90 }, (_, i) => ({
      day: daysBack(89 - i),
      redemptions: Math.max(
        0,
        Math.round(110 + Math.sin(i / 3) * 70 + (i === 30 ? 160 : 0) - (i % 11)),
      ),
    })),
  };
}

/**
 * Deterministic 0–1 scale for a filter selection (comma-separated, as the API
 * receives it). No selection returns 1 — the unfiltered population.
 *
 * Mock data has to *react* to filters, or mock mode renders identically no
 * matter what is selected and a filter that silently sends the wrong value is
 * indistinguishable from one that works. Each selected value contributes its
 * own slice, so both changing a selection and adding one move the numbers.
 */
function filterScale(...selections: (string | undefined)[]): number {
  const values = selections
    .flatMap((s) => (s ? s.split(",") : []))
    .map((v) => v.trim())
    .filter(Boolean);
  if (values.length === 0) return 1;
  const total = values.reduce((sum, value) => {
    const seed = [...value].reduce((acc, c) => acc + c.charCodeAt(0), 0);
    return sum + 0.05 + (seed % 13) / 100;
  }, 0);
  return Math.min(0.95, total);
}

/** Scale + round, keeping mock counts whole. */
function scaled(value: number, scale: number): number {
  return Math.max(1, Math.round(value * scale));
}

/** One comma-separated multi-value param into its trimmed values. */
function splitList(value: string | undefined): string[] {
  return value
    ? value.split(",").map((v) => v.trim()).filter(Boolean)
    : [];
}

export function mockRetailerOverview(
  storeId?: string,
  subcategoryId?: string,
): RetailerOverview {
  const s = filterScale(storeId, subcategoryId);
  return {
    generatedAt: new Date().toISOString(),
    windowDays: 90,
    tiles: {
      totalTransactions: scaled(2_920_408, s),
      loyaltyTransactions: scaled(5_423, s),
      totalUnits: scaled(2_883_382, s),
      estimatedShoppers: scaled(121_684, s),
      loyaltyShoppers: scaled(3_633, s),
      totalSales: Math.round(5_481_357.51 * s * 100) / 100,
    },
    engagement: [
      { bucket: "Light Shoppers", shoppers: scaled(2_950, s), transactions: scaled(6_679, s), units: scaled(42_113, s) },
      { bucket: "Medium Shoppers", shoppers: scaled(560, s), transactions: scaled(4_214, s), units: scaled(16_502, s) },
      { bucket: "Heavy Shoppers", shoppers: scaled(123, s), transactions: scaled(7_514, s), units: scaled(6_801, s) },
    ],
  };
}

export function mockRetailerTraffic(
  storeId?: string,
  subcategoryId?: string,
): RetailerTraffic {
  const scale = filterScale(storeId, subcategoryId);
  const cells = [];
  for (let day = 1; day <= 7; day++) {
    for (let hour = 4; hour <= 22; hour++) {
      // Two-humped retail day: a morning rise and an after-work peak,
      // heavier on weekends.
      const shape =
        Math.exp(-((hour - 11) ** 2) / 18) + Math.exp(-((hour - 17) ** 2) / 8);
      const weekend = day >= 6 ? 1.4 : 1;
      cells.push({
        dayOfWeek: day,
        hour,
        units: scaled(18_000 * shape * weekend + day * 310, scale),
      });
    }
  }
  // One outlier cell like the real data's Friday-9AM spike.
  cells.find((c) => c.dayOfWeek === 5 && c.hour === 9)!.units = scaled(
    178_549,
    scale,
  );
  return { generatedAt: new Date().toISOString(), windowDays: 90, cells };
}

export function mockRetailerCategories(
  storeId?: string,
  subcategoryId?: string,
): RetailerCategories {
  const s = filterScale(storeId);
  const selectedCategories = subcategoryId
    ? subcategoryId.split(",").filter(Boolean).length
    : 0;
  const categories = [
    { category: "Food & Beverage -> Pantry", units: 1_389_402, sales: 763_054.34 },
    { category: "Food & Beverage -> Beverages", units: 811_280, sales: 271_766.4 },
    { category: "Food & Beverage -> Fruits & Vegetables", units: 540_427, sales: 666_230.53 },
    { category: "Food & Beverage -> Breakfast", units: 50_467, sales: 219_243.24 },
    { category: "Tobacco & Alcohol -> Beer, Wine & Liquor", units: 24_743, sales: 190_112.09 },
    { category: "Baby -> Baby Bath & Skin Care", units: 18_211, sales: 40_181.63 },
    { category: "Beauty -> Makeup", units: 10, sales: 22.71 },
    { category: "Health & Wellness -> Sleep Aids & Accessories", units: 2, sales: 15.38 },
    { category: "Automotive -> Car Cleaning Tools & Accessories", units: 3, sales: 10.47 },
    { category: "Sports -> Bicycles, Scooters, and Skates", units: 1, sales: 4.19 },
    { category: "Toys & Games -> Pool & Water Toys", units: 1, sales: 0.99 },
  ];
  return {
    generatedAt: new Date().toISOString(),
    windowDays: 90,
    // A category filter narrows which categories come back, as the real
    // GROUP BY would; a store filter only scales them down.
    categories: (selectedCategories > 0
      ? categories.slice(0, selectedCategories)
      : categories
    ).map((c) => ({
      ...c,
      units: scaled(c.units, s),
      sales: Math.round(c.sales * s * 100) / 100,
    })),
  };
}

export function mockRetailerProducts(
  storeId?: string,
  subcategoryId?: string,
): RetailerProducts {
  const s = filterScale(storeId, subcategoryId);
  const top = [
    { upc: "0000000004048", productName: "GREEN LIMES", units: 719_540 },
    { upc: "0000000004235", productName: "GREEN PLANTAIN", units: 574_858 },
    { upc: "0000000004077", productName: "GREEN LIMES", units: 189_389 },
    { upc: "0000000004306", productName: "LIMON VERDE", units: 173_634 },
    { upc: "0076172098749", productName: "Mazola 100% Pure Corn Oil - 40oz", units: 164_635 },
    { upc: "0000000000001", productName: "TORTILLA MAIZ", units: 137_382 },
    { upc: "0000000004237", productName: "PLANTAIN YELLOW", units: 116_856 },
    { upc: "0000000004046", productName: "FRESH HASS AVOCADO", units: 112_299 },
    { upc: "0000000004612", productName: null, units: 98_204 },
  ];
  const bottom = [
    { upc: "0000000000910", productName: "Svyturys White", units: 1 },
    { upc: "0004130300155", productName: "ESSENTIAL E. PUFFED RICE", units: 1 },
    { upc: "0043000955673", productName: "KOOL-AID Blackberry .13z", units: 1 },
    { upc: "0004722343009", productName: "CANDLE APPLE & CINNAMON", units: 1 },
    { upc: "0004650004063", productName: "GLADE AIR FRESH ROSE/BLOOM", units: 1 },
    { upc: "8681619001125", productName: "SANTE FD ORG DRIED MULBERRIES", units: 1 },
    { upc: "0005480042527", productName: "BEN'S WHT RICE LNG GRAIN", units: 1 },
    { upc: "0007956710035", productName: "3-IN-ONE OIL", units: 1 },
  ];
  return {
    generatedAt: new Date().toISOString(),
    windowDays: 90,
    rankLimit: 1000,
    top: top.map((p) => ({ ...p, units: scaled(p.units, s) })),
    bottom,
  };
}

/** Monday-start week containing the day `n` days back. */
function weeksBack(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  const isoDow = d.getDay() === 0 ? 7 : d.getDay();
  d.setDate(d.getDate() - (isoDow - 1));
  return d.toISOString().slice(0, 10);
}

const BRAND_NAMES = [
  "Coca-Cola",
  "Carnation",
  "Goya",
  "Libby's",
  "Mazola",
  "Minute Maid",
  "Nature's Own",
];

const DMA_NAMES = [
  "Boston (Manchester)",
  "Fort Myers-Naples",
  "Harrisburg-Lancaster-Lebanon-York",
  "Miami-Fort Lauderdale",
  "New York",
  "Orlando-Daytona Beach-Melbourne",
  "Tampa-St. Petersburg (Sarasota)",
  "West Palm Beach-Fort Pierce",
];

const MOCK_CAMPAIGNS = [
  { campaignId: "3001", campaignName: "Coke Zero $1 Off", accountName: "Coca-Cola Co.", campaignType: "DOLLAR_OFF", rewardValue: 1, budget: 25_000, spend: 16_400, beginDate: daysBack(20), endDate: daysBack(-10) },
  { campaignId: "3002", campaignName: "Nescafe TPR", accountName: "Nestle", campaignType: "TPR", rewardValue: 0.75, budget: null, spend: 4_200, beginDate: daysBack(48), endDate: daysBack(18) },
  { campaignId: "3003", campaignName: "Adobo Launch Promo", accountName: "Goya Foods", campaignType: "DOLLAR_OFF", rewardValue: 0.5, budget: 10_000, spend: 9_850, beginDate: daysBack(75), endDate: daysBack(45) },
  { campaignId: "3004", campaignName: "Plugin Validation", accountName: null, campaignType: "DOLLAR_OFF", rewardValue: 0.05, budget: 100, spend: 12, beginDate: daysBack(10), endDate: daysBack(-20) },
];

export function mockProductFilters(): ProductFilters {
  return {
    generatedAt: new Date().toISOString(),
    brands: BRAND_NAMES.map((brandName, i) => ({
      brandId: String(500 + i),
      brandName,
    })),
    accounts: ["Coca-Cola Co.", "Nestle", "Goya Foods", "GFT Validation"].map(
      (accountName, i) => ({ accountId: String(9000 + i), accountName }),
    ),
    dmas: DMA_NAMES,
    categories: [
      "Food & Beverage -> Beverages",
      "Food & Beverage -> Breakfast",
      "Food & Beverage -> Fruits & Vegetables",
      "Food & Beverage -> Pantry",
    ].map((categoryName, i) => ({ categoryId: String(200 + i), categoryName })),
    level1Categories: [
      "Baby",
      "Beauty",
      "Food & Beverage",
      "Health & Wellness",
      "Tobacco & Alcohol",
    ].map((categoryName, i) => ({ categoryId: String(100 + i), categoryName })),
    stores: STORE_NAMES.map((storeName, i) => ({
      storeId: String(1000 + i),
      storeName,
    })),
    campaigns: MOCK_CAMPAIGNS.map(
      ({ campaignId, campaignName, accountName, beginDate, endDate }) => ({
        campaignId,
        campaignName,
        accountName,
        beginDate,
        endDate,
      }),
    ),
  };
}

const DEMAND_PRODUCTS = [
  { upc: "0004900000044", productName: "COCA COLA", units: 3_548 },
  { upc: "0004900004779", productName: "COCA COLA MEX. 24/16 OZ. 16FL", units: 1_375 },
  { upc: "0004900005010", productName: "COCA COLA 2 LT", units: 1_132 },
  { upc: "0004900005341", productName: "COCA COLA CANS BIG", units: 1_084 },
  { upc: "0004900000634", productName: "COCA COLA CAN", units: 904 },
  { upc: "0004900000045", productName: "DIET COCA COLA", units: 827 },
  { upc: "0004900002890", productName: "COCA COLA 12 PACK", units: 667 },
  { upc: "0000075004699", productName: "COCA COLA 500ML", units: 552 },
  { upc: "0004900004086", productName: "COCA COLA ZERO", units: 442 },
  { upc: "0000000004048", productName: "GREEN LIMES", units: 719 },
  { upc: "0000000000001", productName: null, units: 137 },
];

export function mockProductDemand(
  q?: string,
  filters: {
    storeId?: string;
    brandId?: string;
    categoryId?: string;
    subcategoryId?: string;
  } = {},
): ProductDemand {
  const needle = q?.trim().toUpperCase();
  const scale = filterScale(
    filters.storeId,
    filters.brandId,
    filters.categoryId,
    filters.subcategoryId,
  );
  const matched = needle
    ? DEMAND_PRODUCTS.filter((p) => p.productName?.includes(needle))
    : DEMAND_PRODUCTS;
  const products = matched.map((p) => ({
    ...p,
    units: scaled(p.units, scale),
  }));
  const total = products.reduce((s, p) => s + p.units, 0);
  return {
    generatedAt: new Date().toISOString(),
    windowDays: 30,
    productLimit: 500,
    capped: false,
    unitsByDay: Array.from({ length: 30 }, (_, i) => ({
      day: daysBack(29 - i),
      units: Math.max(
        5,
        Math.round(total / 30 + Math.sin(i / 3) * (total / 90)),
      ),
    })),
    products,
    byStore: STORE_NAMES.slice(0, 14).map((storeName, i) => ({
      storeName,
      units: Math.max(40, Math.round(total / 4) - i * Math.round(total / 60)),
    })),
    byDma: DMA_NAMES.map((dma, i) => ({
      dma,
      units: Math.max(120, Math.round(total / 2) - i * Math.round(total / 16)),
    })),
  };
}

const CATALOG_ROWS = [
  { upc: "0000000004048", checkDigit: "8", productName: "GREEN LIMES", brandName: null, categoryName: "Food & Beverage", subcategoryName: "Food & Beverage -> Fruits & Vegetables" },
  { upc: "0049000042566", checkDigit: "6", productName: "COCA-COLA ZERO", brandName: "Coca-Cola", categoryName: "Food & Beverage", subcategoryName: "Food & Beverage -> Beverages" },
  { upc: "0076172098749", checkDigit: "9", productName: "Mazola 100% Pure Corn Oil - 40oz", brandName: "Mazola", categoryName: "Food & Beverage", subcategoryName: "Food & Beverage -> Pantry" },
  { upc: "TPW.PRIIS.COM", checkDigit: null, productName: "PSY NONI SHAMPOO", brandName: null, categoryName: null, subcategoryName: null },
  { upc: "TOPUPRECHARGE", checkDigit: null, productName: "Top Up recharge", brandName: null, categoryName: null, subcategoryName: null },
  { upc: "B379512804384", checkDigit: null, productName: "TAX GROC N/FD", brandName: null, categoryName: null, subcategoryName: null },
  { upc: "9872412819065", checkDigit: null, productName: "KHAN LUMPN CRAB MEAT", brandName: null, categoryName: null, subcategoryName: null },
  { upc: "0000000000001", checkDigit: null, productName: "TORTILLA MAIZ", brandName: null, categoryName: null, subcategoryName: null },
];

export function mockCatalogSearch(q?: string): CatalogSearch {
  const needle = q?.trim().toLowerCase();
  const rows = needle
    ? CATALOG_ROWS.filter(
        (r) =>
          r.upc.toLowerCase().includes(needle) ||
          r.productName?.toLowerCase().includes(needle),
      )
    : CATALOG_ROWS;
  return {
    generatedAt: new Date().toISOString(),
    rowLimit: 500,
    capped: false,
    rows,
  };
}

export function mockCampaignImpact(
  campaignIdInput: string,
  upcsInput?: string,
): CampaignImpact {
  const ids = new Set(splitList(campaignIdInput));
  const matched = MOCK_CAMPAIGNS.filter((c) => ids.has(c.campaignId));
  const selected = matched.length > 0 ? matched : [MOCK_CAMPAIGNS[0]];
  // The UPC filter scopes units only — redemptions must not react to it,
  // mirroring the endpoint (redemptions carry no UPC).
  const scale = filterScale(campaignIdInput);
  const unitScale = filterScale(campaignIdInput, upcsInput);

  const baselineDays = 28;
  const campaignDays = 21;
  const totalDays = baselineDays + campaignDays;
  const windowStart = daysBack(totalDays - 1);
  const campaignStart = daysBack(campaignDays - 1);
  const windowEnd = daysBack(0);

  // Units cover only the request's UPCs; none entered means no units data.
  const upcs = splitList(upcsInput)
    .map((u) => u.replace(/^0+/, "").padStart(13, "0"))
    .slice(0, MAX_UPCS)
    .sort();
  const hasUpcs = upcs.length > 0;

  // Flat baseline, then a visible lift once the campaign starts.
  const trend = Array.from({ length: totalDays }, (_, i) => {
    const day = daysBack(totalDays - 1 - i);
    const inCampaign = i >= baselineDays;
    return {
      day,
      redemptions: inCampaign
        ? scaled(28 + Math.sin(i / 3) * 9 - (i % 7), scale)
        : 0,
      units: hasUpcs
        ? scaled((inCampaign ? 260 : 120) + Math.sin(i / 4) * 30, unitScale)
        : 0,
    };
  });

  const totalRedemptions = trend.reduce((sum, d) => sum + d.redemptions, 0);
  const campaignUnits = trend
    .filter((d) => d.day >= campaignStart)
    .reduce((sum, d) => sum + d.units, 0);
  const baselineUnits = trend
    .filter((d) => d.day < campaignStart)
    .reduce((sum, d) => sum + d.units, 0);
  const averageDailyUnits = {
    baseline: Math.round((baselineUnits / baselineDays) * 100) / 100,
    campaign: Math.round((campaignUnits / campaignDays) * 100) / 100,
  };

  const stores = STORE_NAMES.slice(0, 10);
  const storeWeights = stores.map((_, i) => stores.length - i);
  const weightTotal = storeWeights.reduce((sum, w) => sum + w, 0);
  const byStore = stores.map((storeName, i) => ({
    storeId: String(1000 + i),
    storeName,
    redemptions: Math.round((totalRedemptions * storeWeights[i]) / weightTotal),
    units: Math.round((campaignUnits * storeWeights[i]) / weightTotal),
  }));

  const byStoreUpc = byStore.flatMap(({ storeId, storeName, units }, i) =>
    upcs.map((upc, j) => ({
      storeId,
      storeName,
      upc,
      minPrice: Math.round((1.49 + j * 1.2 + (i % 3) * 0.3) * 100) / 100,
      units: Math.max(1, Math.round(units / upcs.length) - j * 2),
    })),
  );

  const campaigns = selected.map((c, i) => ({
    campaignId: c.campaignId,
    campaignName: c.campaignName,
    campaignType: c.campaignType,
    beginDate: c.beginDate,
    endDate: c.endDate,
    rewardValue: c.rewardValue,
    budget: c.budget,
    spend: c.spend,
    redemptions: Math.round(totalRedemptions / selected.length) - i,
  }));
  const budgets = campaigns
    .map((c) => c.budget)
    .filter((b): b is number => b != null);

  return {
    generatedAt: new Date().toISOString(),
    baselineDays,
    windowStart,
    campaignStart,
    // Mock redemptions begin exactly at campaignStart, so the boundary
    // never moves earlier here.
    liftStart: campaignStart,
    windowEnd,
    upcs,
    campaigns,
    totals: {
      redemptions: totalRedemptions,
      uniqueShoppers: Math.round(totalRedemptions * 0.62),
      units: campaignUnits,
      spend: campaigns.reduce((sum, c) => sum + c.spend, 0),
      budget:
        budgets.length > 0 ? budgets.reduce((sum, b) => sum + b, 0) : null,
    },
    averageDailyUnits,
    byStore,
    byStoreUpc,
    trend,
    spendByDay: trend
      .filter((d) => d.day >= campaignStart)
      .map((d) => ({
        day: d.day,
        spend: Math.round(d.redemptions * 0.9 * 100) / 100,
      })),
    redemptionsByDma: DMA_NAMES.map((dma, i) => ({
      dma,
      redemptions: Math.max(
        1,
        Math.round(totalRedemptions / 3) - i * Math.round(totalRedemptions / 12),
      ),
    })),
  };
}

export function mockBrandMetrics(
  brandId?: string,
  subcategoryId?: string,
): BrandMetrics {
  // The brand's share of its category, and how much of the catalog the
  // selected categories represent — both react to every added selection.
  const share = filterScale(brandId);
  const categoryShare = filterScale(subcategoryId);
  const byWeek = Array.from({ length: 13 }, (_, i) => {
    const categoryUnits = scaled(
      1_500_000 + Math.round(Math.sin(i / 2) * 250_000),
      categoryShare,
    );
    return {
      weekStart: weeksBack((12 - i) * 7),
      brandUnits: Math.round(categoryUnits * share),
      categoryUnits,
    };
  });
  const byDma = DMA_NAMES.map((dma, i) => {
    const categoryUnits = scaled(7_000_000 - i * 830_000, categoryShare);
    return {
      dma,
      brandUnits: Math.round(categoryUnits * share),
      categoryUnits,
    };
  });
  const totals = byWeek.reduce(
    (acc, w) => ({
      brandUnits: acc.brandUnits + w.brandUnits,
      categoryUnits: acc.categoryUnits + w.categoryUnits,
    }),
    { brandUnits: 0, categoryUnits: 0 },
  );
  return {
    generatedAt: new Date().toISOString(),
    windowDays: 90,
    totals,
    basketPenetration: filterScale(brandId),
    byWeek,
    byDma,
    engagement: [
      { bucket: "Light Shoppers", shoppers: scaled(2_950, share), transactions: scaled(3_141, share), units: scaled(42_113, share) },
      { bucket: "Medium Shoppers", shoppers: scaled(560, share), transactions: scaled(1_419, share), units: scaled(16_502, share) },
      { bucket: "Heavy Shoppers", shoppers: scaled(63, share), transactions: scaled(874, share), units: scaled(6_801, share) },
    ],
  };
}

export function mockUpcMetrics(
  upcsInput: string,
  dma?: string,
  storeId?: string,
): UpcMetrics {
  const upcs = upcsInput
    .split(",")
    .map((u) => u.trim().replace(/^0+/, "").padStart(13, "0"))
    .filter(Boolean)
    .slice(0, MAX_UPCS);
  // Different UPC sets get visibly different series, so comparison views
  // don't render two identical overlapping lines in mock mode.
  const seed = Number(upcs[0]?.slice(-2) ?? "0");
  const scale = (0.4 + ((seed % 9) / 9) * 1.2) * filterScale(dma, storeId);
  const unitsByDay = Array.from({ length: 30 }, (_, i) => ({
    day: daysBack(29 - i),
    units: Math.max(
      10,
      Math.round((160 + Math.sin(i / 3) * 45 - (i % 5) * 8) * scale),
    ),
  }));
  const byStore = STORE_NAMES.slice(0, 10).map((storeName, i) => ({
    storeName,
    units: Math.max(15, Math.round((280 - i * 21) * scale)),
  }));
  return {
    generatedAt: new Date().toISOString(),
    windowDays: 30,
    upcs,
    unitsByDay,
    byStore,
    byStoreUpc: byStore.flatMap(({ storeName, units }, i) =>
      upcs.map((upc, j) => ({
        storeName,
        upc,
        minPrice: Math.round((1.99 + j * 1.1 + (i % 3) * 0.3) * 100) / 100,
        units: Math.max(1, Math.round(units / upcs.length) - j * 3),
      })),
    ),
  };
}

export function mockProductRaw(
  query: {
    storeId?: string;
    brandId?: string;
    subcategoryId?: string;
    dma?: string;
    upcs?: string;
  } = {},
): ProductRaw {
  const products = [
    { brand: null, upc: "0000000004048", productName: "GREEN LIMES", subcategory: "Food & Beverage -> Fruits & Vegetables", units: 51_954, minPrice: 0.25 },
    { brand: null, upc: "0000000004235", productName: "GREEN PLANTAIN", subcategory: "Food & Beverage -> Fruits & Vegetables", units: 41_204, minPrice: 0.33 },
    { brand: "Mazola", upc: "0076172098749", productName: "Mazola 100% Pure Corn Oil - 40oz", subcategory: "Food & Beverage -> Pantry", units: 12_874, minPrice: 6.99 },
    { brand: null, upc: "0000000000001", productName: "TORTILLA MAIZ", subcategory: null, units: 9_812, minPrice: 1.69 },
    { brand: "Coca-Cola", upc: "0049000042566", productName: "COCA-COLA ZERO", subcategory: "Food & Beverage -> Beverages", units: 7_454, minPrice: 1.49 },
    { brand: null, upc: "0000000000002", productName: "LOTTO", subcategory: null, units: 3, minPrice: 0.03 },
  ];
  // dma and upcs arrive as the same values these rows already carry, so they
  // filter for real; brandId/subcategoryId/storeId are catalog IDs the
  // name-based mock rows can't match, so they scale the numbers instead.
  const wantedUpcs = new Set(splitList(query.upcs));
  const wantedDmas = new Set(splitList(query.dma));
  const scale = filterScale(query.storeId, query.brandId, query.subcategoryId);

  const rows = products
    .filter((p) => wantedUpcs.size === 0 || wantedUpcs.has(p.upc))
    .flatMap((p, i) =>
      DMA_NAMES.slice(0, i % 3 === 0 ? 3 : 2).flatMap((dmaName, j) => {
        const dma = i === 3 ? null : dmaName;
        if (wantedDmas.size > 0 && (dma === null || !wantedDmas.has(dma))) {
          return [];
        }
        const units = Math.max(1, scaled(p.units / (j + 1), scale));
        return [
          {
            ...p,
            dma,
            units,
            amount: Math.round(p.minPrice * units * 100) / 100,
          },
        ];
      }),
    );

  return {
    generatedAt: new Date().toISOString(),
    windowDays: 7,
    rowLimit: 5000,
    capped: false,
    rows,
  };
}

export function mockCampaignStats(): CampaignStats {
  const campaigns = [
    { campaignName: "$0.50 off Coca-Cola 20 oz and 2 L bottles - all flavors", campaignType: "DOLLAR_OFF", rewardValue: 0.5, redemptions: 1944, campaignSpend: 4699.25, campaignBudget: 5000, startDate: "2026-05-26", endDate: "2026-10-01" },
    { campaignName: "$0.75 off Coca-Cola 2-Liter Bottle - multiple varieties", campaignType: "DOLLAR_OFF", rewardValue: 0.75, redemptions: 244, campaignSpend: 855.9, campaignBudget: 1000, startDate: "2026-05-20", endDate: "2026-10-01" },
    { campaignName: "$0.25 off Carnation Evaporated Milk 12 Oz", campaignType: "DOLLAR_OFF", rewardValue: 0.25, redemptions: 187, campaignSpend: 526.75, campaignBudget: 650, startDate: "2026-05-26", endDate: "2026-10-01" },
    { campaignName: "$0.50 off Bandi Plombir Ice Cream 125 ml - Vanilla, Chocolate, Red", campaignType: "DOLLAR_OFF", rewardValue: 0.5, redemptions: 123, campaignSpend: 327.5, campaignBudget: 450, startDate: "2026-05-29", endDate: "2026-10-01" },
    { campaignName: "Goya Peas & Carrots 15oz", campaignType: "DOLLAR_OFF", rewardValue: 0.25, redemptions: 84, campaignSpend: 620.15, campaignBudget: 660, startDate: "2026-05-08", endDate: "2026-08-31" },
    { campaignName: "GFT Validation Campaign", campaignType: "DOLLAR_OFF", rewardValue: 0.1, redemptions: 20, campaignSpend: 152, campaignBudget: 200, startDate: "2026-03-24", endDate: "2027-03-25" },
    { campaignName: "5 cents off", campaignType: "TPR", rewardValue: 0.05, redemptions: 3, campaignSpend: 0, campaignBudget: null, startDate: "2026-08-04", endDate: "2026-08-06" },
    { campaignName: "dollar off", campaignType: "TPR", rewardValue: 1, redemptions: 1, campaignSpend: 0, campaignBudget: null, startDate: "2026-08-06", endDate: "2026-09-01" },
  ];

  const redemptionsByCampaignByStore = STORE_NAMES.slice(0, 12).map(
    (storeName, i) => {
      const campaign = campaigns[i % 2];
      const redemptions = 325 - i * 24;
      return {
        storeName,
        campaignName: campaign.campaignName,
        startDate: campaign.startDate,
        endDate: campaign.endDate,
        redemptions,
        earnedByStore: Math.round(redemptions * campaign.rewardValue * 20) / 100,
        // Fees plus the reward value itself (fees mock as 20% of the reward).
        totalPaidToStore:
          Math.round(redemptions * campaign.rewardValue * 120) / 100,
      };
    },
  );

  return {
    generatedAt: new Date().toISOString(),
    redemptionsOverTime: Array.from({ length: 30 }, (_, i) => ({
      day: daysBack(29 - i),
      redemptions: Math.max(
        5,
        Math.round(210 - i * 4 + Math.sin(i / 2) * 40 + (i === 22 ? -80 : 0)),
      ),
    })),
    redemptionsByDma: [
      { dma: "Orlando-Daytona Beach-Melbourne", redemptions: 1030 },
      { dma: "Tampa-St. Petersburg (Sarasota)", redemptions: 601 },
      { dma: "Fort Myers-Naples", redemptions: 445 },
      { dma: "Miami-Fort Lauderdale", redemptions: 437 },
      { dma: "New York", redemptions: 380 },
      { dma: "Boston (Manchester)", redemptions: 12 },
    ],
    campaigns,
    redemptionsByCampaignByStore,
  };
}
